// filepath: /Users/bipul/wspace/ai11_base/ai11_admin/src/pages/Connect/SendTopicNotification.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import ConnectTemplateAutocomplete from '../../components/Connect/ConnectTemplateAutocomplete';
import type { NotificationChannel, NotificationTemplate } from '../../models';
import { 
  useGetNotificationTopicQuery, 
  useSendTopicNotificationMutation,
  useGetNotificationChannelsQuery
} from '../../services/connectApi';

interface SendFormData {
  template: string;
  channel: string;
  scheduled_at: string;
  template_variables: Record<string, string>;
}

const SendTopicNotification: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  
  // Use Redux hooks for API operations
  const { data: topic, isLoading: isLoadingTopic } = useGetNotificationTopicQuery(id || '', {
    skip: !id
  });
  
  const { data: channelsData, isLoading: isLoadingChannels } = useGetNotificationChannelsQuery({});
  const channels = channelsData?.results || [];
  
  const [sendNotification, { isLoading: isSending }] = useSendTopicNotificationMutation();
  
  const loading = isLoadingTopic || isLoadingChannels;
  const sending = isSending;
  
  const validationSchema = Yup.object({
    template: Yup.string().required('Template is required'),
    channel: Yup.string().required('Channel is required'),
    scheduled_at: Yup.string(),
    template_variables: Yup.object()
  });
  
  const formik = useFormik<SendFormData>({
    initialValues: {
      template: '',
      channel: '',
      scheduled_at: new Date(new Date().getTime() + 5 * 60000).toISOString().slice(0, 16),
      template_variables: {}
    },
    validationSchema,
    onSubmit: async (values) => {
      setError(null);
      try {
        await sendNotification({
          topic_id: id || '',
          template_id: values.template,
          channel_id: values.channel,
          scheduled_at: values.scheduled_at ? new Date(values.scheduled_at).toISOString() : null,
          template_variables: values.template_variables
        });
        setSuccess(true);
        setTimeout(() => {
          navigate('/connect#topics');
        }, 2000);
      } catch (error: any) {
        console.error('Error sending notification:', error);
        setError(error.response?.data?.detail || 'Failed to send notification. Please try again.');
      }
    }
  });
  
  // Set default channel if available for the app
  useEffect(() => {
    if (channels.length > 0) {
      const pushChannel = channels.find(
        (c: NotificationChannel) => c.type === 'push'
      );
      
      if (pushChannel && !formik.values.channel) {
        formik.setFieldValue('channel', pushChannel.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  const handleTemplateChange = async (template: NotificationTemplate | null) => {
    setSelectedTemplate(template);
    formik.setFieldValue('template', template?.id || '');
    
    if (template?.id) {
      try {
        // We're still using the direct API call here since we don't have a specific Redux hook for template details
        // In a full refactoring, we would add this endpoint to connectApi.ts as well
        const response = await api.get(`/connect/notification-templates/${template.id}/`);
        const variables = response.data.variables || [];
        setTemplateVariables(variables);
        
        // Initialize template variables
        const varObj: Record<string, string> = {};
        variables.forEach((v: string) => {
          varObj[v] = '';
        });
        formik.setFieldValue('template_variables', varObj);
      } catch (error) {
        console.error('Error fetching template details:', error);
      }
    } else {
      setTemplateVariables([]);
      formik.setFieldValue('template_variables', {});
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect#topics')}
        >
          Back to Topics
        </Button>
      </Box>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send Notification to Topic
        </Typography>
        
        {topic && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1">{topic.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              Firebase topic: {topic.firebase_topic}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              App: {topic.app_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Subscribers: {topic.subscriber_count}
            </Typography>
          </Box>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Notification has been scheduled successfully!
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ConnectTemplateAutocomplete
                label="Notification Template"
                value={selectedTemplate}
                onChange={handleTemplateChange}
                error={formik.touched.template && Boolean(formik.errors.template)}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.channel && Boolean(formik.errors.channel)}>
                <InputLabel id="channel-label">Notification Channel</InputLabel>
                <Select
                  labelId="channel-label"
                  id="channel"
                  name="channel"
                  value={formik.values.channel}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Notification Channel"
                  required
                >
                  {channels.map((channel) => (
                    <MenuItem key={channel.id} value={channel.id}>
                      {channel.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.channel && formik.errors.channel && (
                  <FormHelperText>{formik.errors.channel}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="scheduled_at"
                name="scheduled_at"
                label="Schedule Time"
                type="datetime-local"
                value={formik.values.scheduled_at}
                onChange={formik.handleChange}
                InputLabelProps={{
                  shrink: true
                }}
                helperText="Leave empty to send immediately"
              />
            </Grid>
            
            {templateVariables.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Template Variables
                </Typography>
                <Grid container spacing={2}>
                  {templateVariables.map((variable) => (
                    <Grid item xs={12} md={6} key={variable}>
                      <TextField
                        fullWidth
                        id={`template_variables.${variable}`}
                        name={`template_variables.${variable}`}
                        label={variable}
                        value={formik.values.template_variables[variable] || ''}
                        onChange={formik.handleChange}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
          
          {selectedTemplate && (
            <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Title:</strong> {selectedTemplate.push_title}
              </Typography>
              <Typography variant="body1">
                <strong>Body:</strong> {selectedTemplate.push_body}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate('/connect#topics')}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={sending || !formik.isValid}
            >
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SendTopicNotification;
